/**
 * Utility functions for safely interacting with Service Workers.
 */

/**
 * Checks if the current browser supports service workers and push notifications.
 */
export function isServiceWorkerSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Safely gets the service worker registration with a timeout.
 * Returns null if:
 * - Service workers are not supported
 * - No service worker is registered
 * - The ready promise times out (default 5 seconds)
 * 
 * This prevents hanging when navigator.serviceWorker.ready is called
 * without an active service worker registration.
 */
export async function getServiceWorkerRegistration(
    timeoutMs: number = 5000
): Promise<ServiceWorkerRegistration | null> {
    if (!isServiceWorkerSupported()) {
        return null;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) {
        console.log("[sw-utils] no service worker registered");
        return null;
    }

    try {
        return await Promise.race([
            navigator.serviceWorker.ready,
            new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error("Service worker ready timeout")), timeoutMs)
            )
        ]);
    } catch (err) {
        console.warn("[sw-utils] error getting service worker registration:", err);
        return null;
    }
}
